import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Calendar } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { downloadEmployeePayslip, getAvailablePayslipMonths } from '@/utils/payslipDownload';
import { getCurrentPayslipMonth } from '@/config/payslipConfig';

interface PayslipDownloadDialogProps {
  employeeId: string;
  employeeName: string;
  children: React.ReactNode;
}

export const PayslipDownloadDialog: React.FC<PayslipDownloadDialogProps> = ({
  employeeId,
  employeeName,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>();
  const [selectedYear, setSelectedYear] = useState<number>();
  const [availableMonths, setAvailableMonths] = useState<Array<{month: number, year: number}>>([]);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    // Check admin status once; only admins can access this dialog
    supabase.rpc('check_is_any_admin').then(({ data, error }) => {
      if (error) {
        console.error('Admin check failed', error);
        setIsAdmin(false);
      } else {
        setIsAdmin(!!data);
      }
    });
  }, []);

  useEffect(() => {
    if (open) {
      loadAvailableMonths();
    }
  }, [open, employeeId]);

  const loadAvailableMonths = async () => {
    try {
      const months = await getAvailablePayslipMonths(employeeId);
      setAvailableMonths(months);
      
      // Set current month as default if available (timezone-aware)
      const { year: currentYear, month: currentMonth } = getCurrentPayslipMonth();
      
      const currentAvailable = months.find(m => m.month === currentMonth && m.year === currentYear);
      if (currentAvailable) {
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
      } else if (months.length > 0) {
        setSelectedMonth(months[0].month);
        setSelectedYear(months[0].year);
      }
    } catch (error) {
      console.error('Error loading available months:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der verfügbaren Monate',
        variant: 'destructive'
      });
    }
  };

  const handleDownload = async () => {
    if (!selectedMonth || !selectedYear) {
      toast({
        title: 'Auswahl erforderlich',
        description: 'Bitte wählen Sie einen Monat aus',
        variant: 'destructive'
      });
      return;
    }

    try {
      setDownloading(true);
      await downloadEmployeePayslip(employeeId, selectedMonth, selectedYear);
      
      toast({
        title: 'Download erfolgreich',
        description: `Lohnabrechnung für ${employeeName} (${selectedYear}-${selectedMonth.toString().padStart(2, '0')}) wurde heruntergeladen`
      });
      
      setOpen(false);
    } catch (error) {
      console.error('Error downloading payslip:', error);
      toast({
        title: 'Download fehlgeschlagen',
        description: 'Fehler beim Herunterladen der Lohnabrechnung',
        variant: 'destructive'
      });
    } finally {
      setDownloading(false);
    }
  };

  const getMonthName = (month: number): string => {
    const monthNames = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return monthNames[month - 1];
  };

  if (isAdmin === false) {
    return null;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Lohnabrechnung herunterladen
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Mitarbeiter</label>
            <div className="p-2 bg-muted rounded border">
              {employeeName}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Monat und Jahr</label>
            {availableMonths.length > 0 ? (
              <Select 
                value={selectedMonth && selectedYear ? `${selectedYear}-${selectedMonth}` : ''}
                onValueChange={(value) => {
                  const [year, month] = value.split('-').map(Number);
                  setSelectedYear(year);
                  setSelectedMonth(month);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Monat auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {availableMonths.map(({ month, year }) => (
                    <SelectItem key={`${year}-${month}`} value={`${year}-${month}`}>
                      {getMonthName(month)} {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-2 bg-muted rounded border text-muted-foreground">
                Keine Lohnabrechnungen verfügbar
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleDownload}
              disabled={!selectedMonth || !selectedYear || downloading || availableMonths.length === 0}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Herunterladen...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Herunterladen
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};